import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { supabase } from '../lib/supabase'
import { BACKEND_UPDATE_FILE, isMissingRpc, isMissingSchema } from '../lib/dataErrors'

type BackendState = 'idle' | 'checking' | 'ready' | 'legacy' | 'outdated' | 'offline'
type BackendContextValue = {
  state: BackendState
  message: string
  retry: () => Promise<void>
}

const BackendContext = createContext<BackendContextValue | null>(null)

export function BackendProvider({ children }: { children: ReactNode }) {
  const { user, demo } = useAuth()
  const [state, setState] = useState<BackendState>('idle')

  const check = useCallback(async () => {
    if (demo || !user || !supabase) { setState('idle'); return }
    setState('checking')
    const { data, error } = await supabase.rpc('livroom_v41_status')
    if (!error) {
      const ready = Boolean(data && typeof data === 'object' && 'ready' in data && data.ready)
      setState(ready ? 'ready' : 'outdated')
      return
    }

    if (isMissingRpc(error)) {
      const probes = await Promise.all([
        supabase.from('book_projects').select('id', { head: true, count: 'exact' }).limit(1),
        supabase.from('friendships').select('id', { head: true, count: 'exact' }).limit(1),
        supabase.from('reader_annotations').select('id', { head: true, count: 'exact' }).limit(1)
      ])
      setState(probes.some(result => result.error && isMissingSchema(result.error)) ? 'outdated' : 'legacy')
      return
    }
    setState(isMissingSchema(error) ? 'outdated' : 'offline')
  }, [demo, user])

  useEffect(() => { void check() }, [check])

  const message = state === 'outdated'
    ? `Atualização necessária: execute ${BACKEND_UPDATE_FILE} no Supabase para liberar criação, clubes e leituras compartilhadas.`
    : state === 'legacy'
      ? `A estrutura V4 foi encontrada. Execute ${BACKEND_UPDATE_FILE} para ativar transações seguras, reordenação e leituras de livros publicados no Estúdio.`
    : state === 'offline'
      ? 'Não foi possível conferir a sincronização. Você pode continuar lendo; ações online podem falhar até a conexão voltar.'
      : ''

  const value = useMemo(() => ({ state, message, retry: check }), [state, message, check])
  return <BackendContext.Provider value={value}>{children}</BackendContext.Provider>
}

export function useBackend() {
  const value = useContext(BackendContext)
  if (!value) throw new Error('useBackend must be used inside BackendProvider')
  return value
}
